import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Scale, Wand2, AlertCircle } from 'lucide-react';
import { AnalysisParams } from './types';
import { toast } from 'sonner';

interface PositionSizeMultiplierPanelProps {
  fileId: string | null;
  params: AnalysisParams;
  files: Array<{ id: string; filename: string }>;
  perFileParams: Map<string, AnalysisParams>;
  isGlobal?: boolean;
  onUpdateParam: (key: keyof AnalysisParams, value: any) => void;
  onBulkApplyMultiplier?: (multiplier: number, fileIds: string[]) => void;
}

const PRESET_MULTIPLIERS = [
  { value: 0.25, label: '0.25x' },
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1.0, label: '1x' },
  { value: 1.5, label: '1.5x' },
  { value: 2.0, label: '2x' },
  { value: 3.0, label: '3x' },
  { value: 5.0, label: '5x' },
];

export const PositionSizeMultiplierPanel: React.FC<PositionSizeMultiplierPanelProps> = ({
  fileId,
  params,
  files,
  perFileParams,
  isGlobal = false,
  onUpdateParam,
  onBulkApplyMultiplier,
}) => {
  const [bulkMultiplier, setBulkMultiplier] = useState<number>(1.0);
  const [selectedFilesForBulk, setSelectedFilesForBulk] = useState<Set<string>>(new Set());

  const handleMultiplierChange = (value: number) => {
    if (value <= 0) {
      toast.error('Multiplier must be greater than 0');
      return;
    }
    
    if (value > 100) {
      toast.error('Multiplier too large (max: 100x)');
      return;
    }
    
    onUpdateParam('position_size_multiplier', value);
    
    if (value !== 1.0) {
      toast.success(`Position size multiplier set to ${value}x`);
    }
  };

  const handleBulkApply = () => {
    if (selectedFilesForBulk.size === 0) {
      toast.error('Please select at least one file');
      return;
    }

    if (bulkMultiplier <= 0 || bulkMultiplier > 100) {
      toast.error('Invalid multiplier value');
      return;
    }

    if (onBulkApplyMultiplier) {
      onBulkApplyMultiplier(bulkMultiplier, Array.from(selectedFilesForBulk));
      setSelectedFilesForBulk(new Set());
      toast.success(`Applied ${bulkMultiplier}x multiplier to ${selectedFilesForBulk.size} file(s)`);
    }
  };

  const handleApplyToAll = () => {
    const allFileIds = files.map(f => f.id);
    if (onBulkApplyMultiplier) {
      onBulkApplyMultiplier(bulkMultiplier, allFileIds);
      toast.success(`Applied ${bulkMultiplier}x multiplier to all ${allFileIds.length} files`);
    }
  };

  const toggleFileSelection = (fId: string) => {
    const newSet = new Set(selectedFilesForBulk);
    if (newSet.has(fId)) {
      newSet.delete(fId);
    } else {
      newSet.add(fId);
    }
    setSelectedFilesForBulk(newSet);
  };

  const currentMultiplier = params.position_size_multiplier || 1.0;

  return (
    <div className="space-y-6">
      {/* Single File Multiplier */}
      {!isGlobal && fileId && (
        <Card className="border-primary/20">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Scale className="h-4 w-4" />
              Position Size Multiplier
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Multiply all position sizes by this factor without editing the CSV file
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Label className="text-sm min-w-[120px]">Multiplier:</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.01"
                  max="100"
                  value={currentMultiplier}
                  onChange={(e) => handleMultiplierChange(parseFloat(e.target.value) || 1.0)}
                  className="w-32"
                />
                <span className="text-sm text-muted-foreground">
                  {currentMultiplier !== 1.0 && (
                    <span className="text-primary font-medium">
                      ({currentMultiplier > 1 ? '+' : ''}{((currentMultiplier - 1) * 100).toFixed(0)}%)
                    </span>
                  )}
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                {PRESET_MULTIPLIERS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={currentMultiplier === preset.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleMultiplierChange(preset.value)}
                    className="text-xs"
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>

              {currentMultiplier !== 1.0 && (
                <div className="text-xs bg-primary/10 text-primary p-3 rounded border border-primary/20">
                  <div className="font-medium mb-1">Current Effect:</div>
                  <div>All position sizes in this file will be multiplied by <strong>{currentMultiplier}x</strong></div>
                  {params.quantity_base && (
                    <div className="mt-1">
                      Example: Base quantity {params.quantity_base} → {(params.quantity_base * currentMultiplier).toFixed(4)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Operations */}
      {!isGlobal && onBulkApplyMultiplier && files.length > 1 && (
        <Card className="border-accent/20">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wand2 className="h-4 w-4" />
              Bulk Apply Multiplier
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Label className="text-sm min-w-[120px]">Bulk Multiplier:</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.01"
                  max="100"
                  value={bulkMultiplier}
                  onChange={(e) => setBulkMultiplier(parseFloat(e.target.value) || 1.0)}
                  className="w-32"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {PRESET_MULTIPLIERS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={bulkMultiplier === preset.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBulkMultiplier(preset.value)}
                    className="text-xs"
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>

              <div className="border-t pt-3 space-y-2">
                <Label className="text-sm">Select Files:</Label>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {files.map((file) => {
                    const fileParams = perFileParams.get(file.id);
                    const currentFileMultiplier = fileParams?.position_size_multiplier || 1.0;
                    const isSelected = selectedFilesForBulk.has(file.id);
                    
                    return (
                      <div
                        key={file.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          isSelected ? 'bg-primary/10 border border-primary/20' : 'bg-muted/30 hover:bg-muted/50'
                        }`}
                        onClick={() => toggleFileSelection(file.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleFileSelection(file.id)}
                          className="cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{file.filename}</div>
                          {currentFileMultiplier !== 1.0 && (
                            <div className="text-xs text-muted-foreground">
                              Current: {currentFileMultiplier}x
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkApply}
                  disabled={selectedFilesForBulk.size === 0}
                  className="flex-1"
                >
                  Apply to {selectedFilesForBulk.size} Selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleApplyToAll}
                  className="flex-1"
                >
                  Apply to All {files.length} Files
                </Button>
              </div>

              {selectedFilesForBulk.size > 0 && (
                <div className="text-xs bg-accent/10 text-accent p-3 rounded border border-accent/20">
                  Will apply {bulkMultiplier}x multiplier to {selectedFilesForBulk.size} selected file(s)
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
